import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { MaintenanceAlertDto } from "../../../api/maintenanceApi";
import {
  DataTable,
  createColumn,
  Column,
} from "../../../components/common/DataTable/DataTable";

interface AlertsBoardProps {
  alerts: MaintenanceAlertDto[];
  isLoading?: boolean;
  showResolvedAt?: boolean;
}

const alertTypeLabels: Record<string, string> = {
  OilChange: "Oil Change",
  TuneUp: "Tune Up",
  AirDryFilterRecommended: "Air Dry Filter (Recommended)",
  AirDryFilterRequired: "Air Dry Filter (Required)",
  Coolant: "Coolant",
  DpfFilter: "DPF Filter",
  TireTreadDepth: "Tire Tread Depth",
  BrakePadCaution: "Brake Pad (Caution)",
  BrakePadReplace: "Brake Pad (Replace)",
};

const ALL_ALERT_TYPES = Object.keys(alertTypeLabels);

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AlertsBoard: React.FC<AlertsBoardProps> = ({
  alerts,
  isLoading,
  showResolvedAt = false,
}) => {
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const vehicleNames = useMemo(() => {
    const names = [...new Set(alerts.map((a) => a.vehicleName))];
    names.sort();
    return names;
  }, [alerts]);

  const filteredAlerts = useMemo(() => {
    return alerts.filter((a) => {
      if (vehicleFilter && a.vehicleName !== vehicleFilter) return false;
      if (typeFilter && a.alertType !== typeFilter) return false;
      return true;
    });
  }, [alerts, vehicleFilter, typeFilter]);

  const columns: Column[] = useMemo(() => {
    const cols: Column[] = [
      createColumn("vehicleName", "Vehicle", {
        flex: 1.2,
        renderCell: (value: string) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
            {value}
          </Typography>
        ),
      }),
      createColumn("alertType", "Alert Type", {
        flex: 1.2,
        renderCell: (value: string) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {alertTypeLabels[value] || value}
          </Typography>
        ),
      }),
      createColumn("severity", "Severity", {
        width: 120,
        renderCell: (value: string) => (
          <Chip
            icon={
              value === "Critical" ? (
                <ErrorIcon sx={{ fontSize: 14 }} />
              ) : (
                <WarningIcon sx={{ fontSize: 14 }} />
              )
            }
            label={value}
            size="small"
            color={value === "Critical" ? "error" : "warning"}
            variant="outlined"
            sx={{ fontSize: "0.75rem" }}
          />
        ),
      }),
      createColumn("message", "Message", {
        flex: 2.5,
        renderCell: (value: string) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {value}
          </Typography>
        ),
      }),
      createColumn("createdAt", "Created", {
        width: 130,
        renderCell: (value: string) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {formatDate(value)}
          </Typography>
        ),
      }),
    ];

    if (showResolvedAt) {
      cols.push(
        createColumn("resolvedAt", "Resolved", {
          width: 130,
          renderCell: (value: string | null) => (
            <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
              {formatDate(value)}
            </Typography>
          ),
        })
      );
    }

    return cols;
  }, [showResolvedAt]);

  const toolbar = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
      }}
    >
      <TextField
        select
        size="small"
        label="Vehicle"
        value={vehicleFilter}
        onChange={(e) => setVehicleFilter(e.target.value)}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="">All Vehicles</MenuItem>
        {vehicleNames.map((name) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Alert Type"
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">All Types</MenuItem>
        {ALL_ALERT_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {alertTypeLabels[type]}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <DataTable
      items={filteredAlerts}
      columns={columns}
      isLoading={isLoading}
      totalCount={filteredAlerts.length}
      toolbar={toolbar}
      fitContentHeight
    />
  );
};

export default AlertsBoard;
